import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const links = [
  { label: "Mission", href: "#mission" },
  { label: "Who We Serve", href: "#serve" },
  { label: "Services", href: "#services" },
  { label: "Process", href: "#process" },
  { label: "Contact", href: "#contact" },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-background/90 backdrop-blur-md border-b border-border" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="logo-link"
          onClick={(e) => { e.preventDefault(); go("#top"); }}
          className={`font-serif text-2xl tracking-tight leading-none ${scrolled ? "text-foreground" : "text-background"}`}
        >
          Fresh Start
          <span className={`block text-[10px] font-sans uppercase tracking-[0.35em] mt-0.5 ${scrolled ? "text-secondary" : "text-primary"}`}>
            Living Services
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
              onClick={() => go(l.href)}
              className={`text-sm font-medium tracking-wide transition-colors duration-300 hover:text-primary ${
                scrolled ? "text-foreground/80" : "text-background/90"
              }`}
            >
              {l.label}
            </button>
          ))}
          <Button
            data-testid="nav-apply-btn"
            onClick={() => go("#apply")}
            className="rounded-full bg-primary text-primary-foreground hover:bg-foreground hover:text-background transition-colors duration-300 px-6"
          >
            Apply Now
          </Button>
        </nav>

        <button
          className={`md:hidden ${scrolled ? "text-foreground" : "text-background"}`}
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-background border-t border-border px-6 py-6 flex flex-col gap-4">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
              className="text-left text-foreground/80 font-medium"
            >
              {l.label}
            </button>
          ))}
          <Button
            data-testid="mobile-apply-btn"
            onClick={() => go("#apply")}
            className="rounded-full bg-primary text-primary-foreground w-full"
          >
            Apply Now
          </Button>
        </div>
      )}
    </motion.header>
  );
};
